import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, queryKeys } from '@/shared/api';

export type MemberRole =
  'owner' | 'lead_moderator' | 'moderator' | 'editor' | 'commenter' | 'member';

export interface Workspace {
  id: string;
  name: string;
  description: string | null;
  /** Header tint (a palette color); `null` = neutral. */
  color: string | null;
  workspace_type: 'personal' | 'team';
  role: MemberRole;
  /** Workspace-level permission identifiers of the viewer. */
  permissions: string[];
  is_owner: boolean;
  /** Set when the viewer's one workspace role is a custom role (then `role` is only a rank). */
  custom_role_id: string | null;
  /** Succession fallback owner; only sent to members who may manage settings. */
  default_board_owner_user_id: string | null;
  created_at: string;
}

/** Workspaces of the viewer; the personal workspace comes first. */
export function useWorkspaces() {
  return useQuery({
    queryKey: queryKeys.workspaces.all,
    queryFn: ({ signal }) => api.get<Workspace[]>('/v1/workspaces', { signal }),
  });
}

export function useWorkspace(workspaceId: string) {
  return useQuery({
    queryKey: queryKeys.workspaces.detail(workspaceId),
    queryFn: ({ signal }) => api.get<Workspace>(`/v1/workspaces/${workspaceId}`, { signal }),
  });
}

/** The caller's default destination of "Create task" (validated by the server). */
export interface QuickCreateDefaults {
  default_board_id: string | null;
  default_column_id: string | null;
}

export function useQuickCreateDefaults(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.quickCreate(workspaceId),
    queryFn: ({ signal }) =>
      api.get<QuickCreateDefaults>(`/v1/workspaces/${workspaceId}/quick-create`, { signal }),
    enabled: enabled && workspaceId !== '',
  });
}

/** Called only when the user chooses "make default"; creating a card never changes it. */
export function useSetQuickCreateDefaults(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data: { board_id: string; column_id: string | null }) =>
      api.put<QuickCreateDefaults>(`/v1/workspaces/${workspaceId}/quick-create`, data),
    onSuccess: (defaults) => {
      client.setQueryData(queryKeys.quickCreate(workspaceId), defaults);
    },
  });
}

export function useClearQuickCreateDefaults(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete(`/v1/workspaces/${workspaceId}/quick-create`),
    onSuccess: () => {
      client.setQueryData<QuickCreateDefaults>(queryKeys.quickCreate(workspaceId), {
        default_board_id: null,
        default_column_id: null,
      });
    },
  });
}

/** Whether the viewer is likely to be able to create tasks in this workspace (the server decides
 * per board; the backend answers 403 for a board where they may not). */
export function canCreateTasks(workspace: Pick<Workspace, 'role' | 'permissions'> | undefined) {
  if (!workspace) return false;
  return (
    workspace.permissions.includes('workspace.create_board') ||
    ['owner', 'lead_moderator', 'moderator', 'editor'].includes(workspace.role)
  );
}
